import React, { useEffect, useState, Fragment, createElement } from 'react';
import { ChevronRight, MailPlus, LinkedinIcon, Clock, DollarSign, Users, Bot, Building, ChevronDown, X, Send, Network, HelpCircle, Code, FileCheck, MessageSquare, Rocket, Settings, Target, Users2, Code2, LifeBuoy, ImageIcon, Palette, Menu, Instagram } from 'lucide-react';
import { Scoping } from './Scoping';
import { WhoIsItFor } from './WhoIsItFor';
import { Routes, Route, MemoryRouter, useNavigate } from 'react-router-dom';
import { LogoGallery } from './pages/LogoGallery';
import { AuthModal } from './components/AuthModal';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { initGTM } from './utils/gtm';
import { GoogleTagManager } from './components/GoogleTagManager';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ThemeToggle } from './components/ThemeToggle';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import api from './utils/axios';
import { Modal, Box, Typography, Button, CircularProgress, TextField } from '@mui/material';
import { ProjectCarousel } from './components/ProjectCarousel';
import { JourneyTimeline } from './components/JourneyTimeline';
const INITIAL_PROMPT = `You are an AI Assistant for "MVPHub." Your goal is to conduct a short, 5-minute conversation with a potential client to gather essential information about their online application project. 
Start by saying: "Hello! I'm MVPHub's AI Assistant. I'll take about 5 minutes to learn more about your online application so we can see if it's possible to build your MVP with zero upfront cost. First, is your idea specifically for an online or digital MVP?"
Follow the conversation flow to gather information about:
1. Project idea (Complexity)
2. Professional background (Experience)
3. Team & resources (Team)
4. Timeline (Urgency)
5. Budget (Funding)
Assign 1-5 ratings for each factor and recommend one of these options:
- 0 cash + 10% equity
- $5K + 5% equity
- $10K + 0% equity
Keep responses concise and friendly.`;
const openCalendly = () => {
  window.open('https://outlook.office.com/bookwithme/user/123d4244166a47d8b3676428b80673b5@mvphub.co/meetingtype/R-iR3fx3sUSwGUgfw_XI8A2?anonymous&ep=mlink', '_blank');
};
const openContract = () => {
  window.open('https://docusign.com/your-contract-link', '_blank');
};
function MainContent() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feasibilityResult, setFeasibilityResult] = useState<boolean | null>(null);
  const [sliderValue, setSliderValue] = useState(0);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState('');
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState<Array<{
    type: 'user' | 'bot';
    content: string;
  }>>([]);
  const [messageInput, setMessageInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const scrollTo = (elementId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById(elementId);
    if (element) {
      const offset = 100; // Offset to account for fixed header and some padding
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  const {
    isDarkMode
  } = useTheme();
  const openFeasibilityCheck = () => {
    // Keep this empty function as it's still used in handleFeasibilityCheck
  };
  const handleFeasibilityCheck = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('https://mvphub.app.n8n.cloud/webhook-test/7825b1ab-84fc-4150-99f0-eb01e1f431e8', {
        prompt: 'New feasibility check request',
        timestamp: new Date().toISOString(),
        source: 'website'
      });
      setApiResponse(response.data.response || 'No response received');
      setIsResponseModalOpen(true);
    } catch (error) {
      console.error('Error during feasibility check:', error);
      setApiResponse('Sorry, there was an error processing your request. Please try again.');
      setIsResponseModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };
  const handleFeasibilityResult = (result: boolean) => {
    setFeasibilityResult(result);
  };
  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
  };
  const calculateCash = (value: number) => {
    return value / 100 * 10000;
  };
  const calculateEquity = (value: number) => {
    return 10 - value / 100 * 10;
  };
  const getLabelVisibility = (value: number, position: 'left' | 'middle' | 'right') => {
    switch (position) {
      case 'left':
        return value === 0 ? 'opacity-100' : 'opacity-0';
      case 'middle':
        return value === 50 ? 'opacity-100' : 'opacity-0';
      case 'right':
        return value === 100 ? 'opacity-100' : 'opacity-0';
    }
  };
  const faqSections = [{
    category: 'Technical Questions',
    categoryDescription: 'Common questions about our development process and technical capabilities',
    icon: Code2,
    questions: [{
      q: 'What technologies do you use to build MVPs?',
      a: 'We primarily use modern web technologies including React, Node.js, and cloud services. Our tech stack is chosen based on scalability, maintainability, and your specific requirements.'
    }, {
      q: 'How long does it typically take to build an MVP?',
      a: "Most MVPs are completed within 4 weeks. The exact timeline depends on the complexity of your project and the features required. We'll provide a detailed timeline during the scoping phase."
    }, {
      q: 'Can you integrate with existing systems/APIs?',
      a: "Yes, we can integrate with most third-party APIs and existing systems. We'll assess the integration requirements during the scoping phase to ensure compatibility."
    }]
  }, {
    category: 'Support & Maintenance',
    categoryDescription: 'Information about our ongoing support and maintenance services',
    icon: LifeBuoy,
    questions: [{
      q: 'What kind of support do you provide after launch?',
      a: 'We provide 30 days of free support after launch, including bug fixes and minor adjustments. Beyond that, we offer flexible maintenance packages based on your needs.'
    }, {
      q: 'How do you handle bug fixes and updates?',
      a: 'During the first 30 days, all bug fixes are included. We use a ticketing system to track and prioritize issues, ensuring quick resolution of critical problems.'
    }]
  }, {
    category: 'Design & UI/UX',
    categoryDescription: 'Questions about our design process and capabilities',
    icon: Palette,
    questions: [{
      q: 'Do you provide UI/UX design services?',
      a: 'Yes, our service includes full UI/UX design. We follow modern design principles and practices to create intuitive, user-friendly interfaces that align with your brand.'
    }, {
      q: 'Can you match our existing brand guidelines?',
      a: "Absolutely. We can work with your existing brand guidelines to ensure consistency across your digital presence. If you don't have guidelines, we can help establish them."
    }]
  }, {
    category: 'General Questions',
    categoryDescription: 'Common questions about our service, process, and terms',
    icon: HelpCircle,
    questions: [{
      q: 'How does the equity-based payment model work?',
      a: "In the equity model, we accept equity in your company instead of cash payment. The exact percentage is negotiated based on your project's scope and potential. This typically ranges from 5-10% equity.\n\nWe become long-term partners in your success, providing ongoing support and guidance beyond the initial MVP development."
    }, {
      q: 'What happens if we need changes during development?',
      a: "We follow an agile development process that allows for changes and pivots. Minor changes are included in the scope, while major changes may require adjustment to the timeline or terms.\n\nWe'll always communicate openly about how changes impact the project schedule and deliverables."
    }]
  }, {
    category: 'Assets & Resources',
    categoryDescription: 'Information about project assets and resource management',
    icon: FileCheck,
    questions: [{
      q: 'Who owns the code and design assets?',
      a: 'You own 100% of the custom code and design assets created specifically for your project. We provide all source files upon project completion.\n\nThe only exception is our proprietary development framework, for which you receive a lifetime usage license.'
    }, {
      q: 'Do you provide documentation?',
      a: 'Yes, we provide comprehensive documentation including:\n\n• Technical documentation\n• API documentation\n• User guides\n• Deployment instructions'
    }]
  }];
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 600,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    maxHeight: '80vh',
    overflow: 'auto'
  };
  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;
    if (message !== INITIAL_PROMPT) {
      setMessages(prev => [...prev, {
        type: 'user',
        content: message
      }]);
    }
    setMessageInput('');
    setIsThinking(true);
    try {
      const response = await api.post('https://mvphub.app.n8n.cloud/webhook-test/7825b1ab-84fc-4150-99f0-eb01e1f431e8', {
        prompt: message,
        context: messages
      });
      const botResponse = response.data.response || 'No response received';
      setMessages(prev => [...prev, {
        type: 'bot',
        content: botResponse
      }]);
      if (botResponse.includes('recommend') || botResponse.includes('pricing option')) {
        setFeasibilityResult(true);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        type: 'bot',
        content: 'Error: Unable to fetch response'
      }]);
    } finally {
      setIsThinking(false);
    }
  };
  const MetricsBar = ({
    metrics
  }) => <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-0">
      <div className="text-center" data-id="element-1">
        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2">Time</p>
        <p className="font-medium text-gray-900 dark:text-white" data-id="element-3">
          {metrics.time}
        </p>
      </div>
      <div className="text-center" data-id="element-4">
        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5">Deliverable</p>
        <p className="font-medium text-gray-900 dark:text-white" data-id="element-6">
          {metrics.output}
        </p>
      </div>
      <div className="text-center" data-id="element-7">
        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-8">Next</p>
        <p className="font-medium text-gray-900 dark:text-white" data-id="element-9">
          {metrics.nextStep}
        </p>
      </div>
    </div>;
  const StepDetailCard = ({
    step,
    position
  }) => {
    if (!step) return null;
    return <div className={`
          absolute z-20 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 
          dark:border-gray-700 p-6 transition-all duration-300 transform
          ${position === 'right' ? 'left-full ml-8' : 'right-full mr-8'}
        `} data-id="element-10">
        <div className="space-y-4" data-id="element-11">
          <div className="flex items-center gap-3" data-id="element-12">
            <step.icon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-13" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-14">
              {step.title}
            </h4>
          </div>
          <p className="text-gray-600 dark:text-gray-300" data-id="element-15">{step.description}</p>
          <div className="space-y-2" data-id="element-16">
            {step.expandedContent.details.map((detail, i) => <div key={i} className="flex items-center text-gray-600 dark:text-gray-300" data-id="element-17">
                <span className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full mr-2" data-id="element-18" />
                {detail}
              </div>)}
          </div>
          <MetricsBar metrics={step.expandedContent.metrics} data-id="element-19" />
        </div>
      </div>;
  };
  return <div className="w-full min-h-screen bg-white dark:bg-gray-900" data-id="element-20">
      <nav className="fixed top-0 w-full bg-white dark:bg-gray-900 border-b dark:border-gray-700 z-50" data-id="element-21">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22">
          <div className="flex justify-between h-16 items-center" data-id="element-23">
            <div onClick={() => navigate('/')} className="text-xl font-bold text-blue-600 cursor-pointer hover:text-blue-700 transition-colors" data-id="element-24">
              <span data-id="element-25">MVP</span>
              <span data-id="element-26">Hub.co</span>
            </div>
            <div className="hidden md:flex space-x-8 items-center" data-id="element-27">
              <a href="#pricing-models" onClick={scrollTo('pricing-models')} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors" data-id="element-28">
                Pricing
              </a>
              <a href="#how-it-works" onClick={scrollTo('how-it-works')} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors" data-id="element-29">
                How it Works
              </a>
              <a href="#why-us" onClick={scrollTo('why-us')} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors" data-id="element-30">
                Why Us
              </a>
              <a href="#faq" onClick={scrollTo('faq')} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors" data-id="element-31">
                FAQs
              </a>
              <a onClick={() => navigate('/who-is-it-for')} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors cursor-pointer" data-id="element-32">
                Who is it for?
              </a>
              <ThemeToggle data-id="element-33" />
              <a href="https://app.mvphub.co/login" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors" data-id="element-34">
                Sign In
              </a>
            </div>
            <button onClick={() => setIsMobileMenuOpen(true)} className="md:hidden p-2 text-gray-600 hover:text-gray-900" data-id="element-35">
              <Menu className="w-6 h-6" data-id="element-36" />
            </button>
          </div>
        </div>
        {isMobileMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden" data-id="element-37">
            <div className="fixed inset-y-0 right-0 w-full bg-white dark:bg-gray-900 shadow-xl" data-id="element-38">
              <div className="flex justify-between items-center p-4 border-b dark:border-gray-700" data-id="element-39">
                <div className="text-xl font-bold text-blue-600 dark:text-blue-400" data-id="element-40">
                  <span data-id="element-41">MVP</span>
                  <span data-id="element-42">Hub.co</span>
                </div>
                <div className="flex items-center space-x-4" data-id="element-43">
                  <ThemeToggle data-id="element-44" />
                  <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" data-id="element-45">
                    <X className="w-6 h-6" data-id="element-46" />
                  </button>
                </div>
              </div>
              <div className="p-4 space-y-4" data-id="element-47">
                <a href="#pricing-models" onClick={e => {
              scrollTo('pricing-models')(e);
              setIsMobileMenuOpen(false);
            }} className="block py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" data-id="element-48">
                  Pricing
                </a>
                <a href="#how-it-works" onClick={e => {
              scrollTo('how-it-works')(e);
              setIsMobileMenuOpen(false);
            }} className="block py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" data-id="element-49">
                  How it Works
                </a>
                <a href="#why-us" onClick={e => {
              scrollTo('why-us')(e);
              setIsMobileMenuOpen(false);
            }} className="block py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" data-id="element-50">
                  Why Us
                </a>
                <a href="#faq" onClick={e => {
              scrollTo('faq')(e);
              setIsMobileMenuOpen(false);
            }} className="block py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" data-id="element-51">
                  FAQs
                </a>
                <a onClick={() => {
              navigate('/who-is-it-for');
              setIsMobileMenuOpen(false);
            }} className="block py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 cursor-pointer" data-id="element-52">
                  Who is it for?
                </a>
                <a href="https://app.mvphub.co/login" className="block py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" data-id="element-53">
                  Sign In
                </a>
              </div>
            </div>
          </div>}
      </nav>
      <section className="pt-36 pb-24 bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800" data-id="element-54">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-55">
          <div className="text-center max-w-4xl mx-auto" data-id="element-56">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-8 leading-tight" data-id="element-57">
              You've Always Had an{' '}
              <span className="relative inline-block" data-id="element-58">
                <span className="relative z-10 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 text-transparent bg-clip-text px-3" data-id="element-59">
                  IDEA
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-100 via-blue-50 to-blue-100 dark:from-blue-900 dark:via-blue-800 dark:to-blue-900 transform -skew-y-3 rounded-lg" data-id="element-60" />
              </span>
              !
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-12 leading-relaxed" data-id="element-61">
              What if we could bring it to life in just{' '}
              <span className="relative inline-block" data-id="element-62">
                <span className="relative z-10 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 text-transparent bg-clip-text font-bold px-3" data-id="element-63">
                  4 WEEKS
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-100 via-blue-50 to-blue-100 dark:from-blue-900 dark:via-blue-800 dark:to-blue-900 transform -skew-y-3 rounded-lg" data-id="element-64" />
              </span>{' '}
              with{' '}
              <span className="relative inline-block" data-id="element-65">
                <span className="relative z-10 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 text-transparent bg-clip-text font-bold px-3" data-id="element-66">
                  ZERO
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-100 via-blue-50 to-blue-100 dark:from-blue-900 dark:via-blue-800 dark:to-blue-900 transform -skew-y-3 rounded-lg" data-id="element-67" />
              </span>{' '}
              upfront costs?
            </p>
            <div id="pricing-models" className="max-w-3xl mx-auto mb-16" data-id="element-68">
              <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white" data-id="element-69">
                Flexible Pricing Models
                <span className="block text-lg font-medium text-gray-600 dark:text-gray-400 mt-1" data-id="element-70">
                  (Cash and/or Equity)
                </span>
              </h2>
              <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur p-10 rounded-2xl shadow-lg" data-id="element-71">
                <div className="flex justify-between mb-3 font-medium" data-id="element-72">
                  <span className="text-lg text-gray-900 dark:text-white" data-id="element-73">
                    ${sliderValue / 10}K cash
                  </span>
                  <span className="text-lg text-gray-900 dark:text-white" data-id="element-74">
                    $10K
                  </span>
                </div>
                <div className="relative py-4" data-id="element-75">
                  <div className="absolute left-0 right-0 top-1/2 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full -translate-y-1/2" data-id="element-76" />
                  <div className="absolute left-0 top-1/2 h-1.5 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 rounded-full -translate-y-1/2 transition-all duration-300" style={{
                  width: `${sliderValue}%`
                }} data-id="element-77" />
                  <input type="range" className="relative w-full h-7 appearance-none bg-transparent cursor-pointer z-10" min="0" max="100" step="50" value={sliderValue} onChange={e => setSliderValue(Number(e.target.value))} data-id="element-78" />
                </div>
                <div className="flex justify-between mt-3 font-medium" data-id="element-79">
                  <span className="text-lg text-gray-900 dark:text-white" data-id="element-80">
                    {10 - sliderValue / 10}% equity
                  </span>
                  <span className="text-lg text-gray-900 dark:text-white" data-id="element-81">
                    0%
                  </span>
                </div>
                <div className="mt-4 relative h-6" data-id="element-82">
                  <div className={`absolute left-0 text-sm text-gray-600 dark:text-gray-400 transition-opacity duration-300 ${getLabelVisibility(sliderValue, 'left')}`} data-id="element-83">
                    Zero cash
                  </div>
                  <div className={`absolute left-1/2 -translate-x-1/2 text-sm text-gray-600 dark:text-gray-400 transition-opacity duration-300 ${getLabelVisibility(sliderValue, 'middle')}`} data-id="element-84">
                    Balanced
                  </div>
                  <div className={`absolute right-0 text-sm text-gray-600 dark:text-gray-400 transition-opacity duration-300 ${getLabelVisibility(sliderValue, 'right')}`} data-id="element-85">
                    Full cash
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-6 text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg" data-id="element-86">
              In just 5 minutes, discover whether we can build your project,
              what it will cost, and your expected timeline.
            </p>
            <div className="w-full max-w-3xl mx-auto h-[600px] rounded-xl overflow-hidden shadow-md mt-8" data-id="element-87">
              <iframe src={`https://app.mvphub.co/embed/chat?defaultTheme=${isDarkMode ? 'dark' : 'light'}`} className="w-full h-full" data-id="element-88"></iframe>
            </div>
          </div>
        </div>
      </section>
      <ProjectCarousel data-id="element-89" />
      <JourneyTimeline data-id="element-90" />
      <section id="why-us" className="py-20 bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800" data-id="element-91">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-92">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900 dark:text-white" data-id="element-93">
            Why Choose Us?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-center mb-16 text-lg" data-id="element-94">
            We blend experience with innovation to create high-impact MVPs.
          </p>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-95">
            {[{
            icon: DollarSign,
            title: 'Cost-Effective',
            description: 'From $0 upfront to a maximum of $10K.',
            bgColor: 'bg-blue-50'
          }, {
            icon: Clock,
            title: 'Lightning-Fast',
            description: '4-week delivery—get to market quickly.',
            bgColor: 'bg-green-50'
          }, {
            icon: Users,
            title: 'Real Team',
            description: 'Senior consultants with hands-on expertise.',
            bgColor: 'bg-purple-50'
          }, {
            icon: Bot,
            title: 'AI-Powered',
            description: 'Leveraging the latest technology to accelerate development.',
            bgColor: 'bg-yellow-50'
          }, {
            icon: Building,
            title: 'Proven Experience',
            description: '100+ successful projects delivered.',
            bgColor: 'bg-red-50'
          }, {
            icon: Network,
            title: 'Growth Connections',
            description: 'Access a curated network for marketing, recruitment, branding, customer support, and more.',
            bgColor: 'bg-indigo-50'
          }].map(({
            icon: Icon,
            title,
            description,
            bgColor
          }, index) => <div key={index} className="transform transition-all duration-200 hover:scale-105" data-id="element-96">
                <div className={`${bgColor} p-8 rounded-2xl h-full border border-gray-100 shadow-sm`} data-id="element-97">
                  <div className="flex items-center mb-4" data-id="element-98">
                    <div className="bg-white w-12 h-12 rounded-xl flex items-center justify-center shadow-sm" data-id="element-99">
                      <Icon className="w-6 h-6 text-blue-600" data-id="element-100" />
                    </div>
                    <h3 className="text-xl font-semibold ml-4" data-id="element-101">{title}</h3>
                  </div>
                  <p className="text-gray-600" data-id="element-102">{description}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>
      <section id="faq" className="py-20 bg-gray-50/95 dark:bg-gray-800/95 backdrop-blur" data-id="element-103">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-104">
          <h2 className="text-3xl font-bold text-center mb-4 text-gray-900 dark:text-white" data-id="element-105">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-center mb-16 text-lg" data-id="element-106">
            Everything you need to know about MVPHub and our services
          </p>
          <div className="max-w-4xl mx-auto space-y-12" data-id="element-107">
            {faqSections.map((section, sectionIndex) => <div key={sectionIndex} className="space-y-6" data-id="element-108">
                <div className="border-b border-gray-200 dark:border-gray-700 pb-4" data-id="element-109">
                  <div className="flex items-center gap-3 mb-2" data-id="element-110">
                    <div className="bg-blue-50 dark:bg-blue-900/50 p-2 rounded-lg" data-id="element-111">
                      <section.icon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-112" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-113">
                      {section.category}
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-114">
                    {section.categoryDescription}
                  </p>
                </div>
                <div className="space-y-4" data-id="element-115">
                  {section.questions.map((faq, index) => {
                const [isOpen, setIsOpen] = useState(false);
                return <div key={index} className="bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-116">
                        <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left" aria-expanded={isOpen} data-id="element-117">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white pr-8" data-id="element-118">
                            {faq.q}
                          </h4>
                          <ChevronDown className={`w-5 h-5 text-gray-500 dark:text-gray-400 transform transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-119" />
                        </button>
                        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[2000px]' : 'max-h-0'}`} data-id="element-120">
                          <div className="px-6 pb-4 pt-2 text-gray-600 dark:text-gray-300" data-id="element-121">
                            {faq.table ? <div className="mt-4" data-id="element-122">
                                <div className="grid grid-cols-3 gap-4" data-id="element-123">
                                  <div className="font-semibold text-gray-900 dark:text-white" data-id="element-124">
                                    Platform
                                  </div>
                                  <div className="font-semibold text-gray-900 dark:text-white" data-id="element-125">
                                    Pros
                                  </div>
                                  <div className="font-semibold text-gray-900 dark:text-white" data-id="element-126">
                                    Cons
                                  </div>
                                  {faq.a.split('\n').filter(line => line.includes('|')).map((row, i) => {
                            const [platform, pros, cons] = row.split('|').map(s => s.trim());
                            return <Fragment key={i} data-id="element-127">
                                          <div className="py-2 border-t border-gray-100" data-id="element-128">
                                            {platform}
                                          </div>
                                          <div className="py-2 border-t border-gray-100" data-id="element-129">
                                            {pros}
                                          </div>
                                          <div className="py-2 border-t border-gray-100" data-id="element-130">
                                            {cons}
                                          </div>
                                        </Fragment>;
                          })}
                                </div>
                                <p className="mt-4" data-id="element-131">
                                  We don't just build an MVP—we help define the
                                  business logic, ensure scalability, and
                                  provide human expertise at every step. Unlike
                                  no-code platforms, you're not locked into a
                                  system that limits growth or forces you to
                                  rebuild later.
                                </p>
                              </div> : <div className="prose prose-gray dark:prose-invert max-w-none" data-id="element-132">
                                {faq.a.split('\n').map((paragraph, i) => <p key={i} className={i > 0 ? 'mt-4' : ''} data-id="element-133">
                                    {paragraph}
                                  </p>)}
                              </div>}
                          </div>
                        </div>
                      </div>;
              })}
                </div>
              </div>)}
          </div>
        </div>
      </section>
      <footer className="border-t py-8" data-id="element-134">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-135">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0" data-id="element-136">
            <div className="flex flex-col items-center md:items-start space-y-4" data-id="element-137">
              <div className="text-blue-600 font-bold" data-id="element-138">
                <span data-id="element-139">MVP</span>
                <span data-id="element-140">Hub.co</span>
              </div>
              <div className="flex space-x-5" data-id="element-141">
                <a href="mailto:support@mvphub.co" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" aria-label="Email us" data-id="element-142">
                  <MailPlus className="w-4 h-4" strokeWidth={1.5} data-id="element-143" />
                </a>
                <a href="https://www.linkedin.com/company/mvphub-co" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-id="element-144">
                  <LinkedinIcon className="w-4 h-4" strokeWidth={1.5} data-id="element-145" />
                </a>
                <a href="https://www.instagram.com/mvphub.co/" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-id="element-146">
                  <Instagram className="w-4 h-4" strokeWidth={1.5} data-id="element-147" />
                </a>
                <a onClick={() => navigate('/logo-gallery')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer" aria-label="View Logo Gallery" data-id="element-148"></a>
              </div>
              <p className="text-sm text-gray-500" data-id="element-149">
                © 2025 MVPHub.co. All rights reserved.
              </p>
            </div>
            <div className="flex space-x-6 text-sm text-gray-500" data-id="element-150">
              <a onClick={() => navigate('/terms')} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors cursor-pointer" data-id="element-151">
                Terms
              </a>
              <a onClick={() => navigate('/privacy')} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors cursor-pointer" data-id="element-152">
                Privacy
              </a>
            </div>
          </div>
        </div>
      </footer>
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} onSuccess={handleAuthSuccess} data-id="element-153" />
      <Modal open={isResponseModalOpen} onClose={() => setIsResponseModalOpen(false)} aria-labelledby="chatgpt-response-modal" data-id="element-154">
        <Box sx={modalStyle} data-id="element-155">
          <Typography variant="h6" component="h2" mb={2} data-id="element-156">
            ChatGPT Response
          </Typography>
          <Typography sx={{
          whiteSpace: 'pre-wrap',
          mb: 3
        }} data-id="element-157">
            {apiResponse}
          </Typography>
          <Button variant="contained" onClick={() => setIsResponseModalOpen(false)} sx={{
          mt: 2
        }} data-id="element-158">
            Close
          </Button>
        </Box>
      </Modal>
    </div>;
}
export function App() {
  useEffect(() => {
    initGTM();
    (function () {
      var w = window as any;
      var ic = w.Intercom;
      if (typeof ic === 'function') {
        ic('reattach_activator');
        ic('update', w.intercomSettings);
      } else {
        var d = document;
        var i = function (...args: any[]) {
          i.c(args);
        } as any;
        i.q = [] as any[];
        i.c = function (args: any) {
          i.q.push(args);
        };
        w.Intercom = i;
        var l = function () {
          var s = d.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = 'https://widget.intercom.io/widget/a8g4trmj';
          var x = d.getElementsByTagName('script')[0];
          x.parentNode!.insertBefore(s, x);
        };
        if (document.readyState === 'complete') {
          l();
        } else if (w.attachEvent) {
          w.attachEvent('onload', l);
        } else {
          w.addEventListener('load', l, false);
        }
      }
    })();
    (window as any).Intercom('boot', {
      app_id: 'a8g4trmj'
    });
  }, []);
  return <ThemeProvider data-id="element-159">
      <MUIThemeProvider theme={theme} data-id="element-160">
        <CssBaseline data-id="element-161" />
        <MemoryRouter data-id="element-162">
          <GoogleTagManager data-id="element-163" />
          <Routes data-id="element-164">
            <Route path="/" element={<MainContent data-id="element-166" />} data-id="element-165" />
            <Route path="/scoping" element={<Scoping data-id="element-168" />} data-id="element-167" />
            <Route path="/who-is-it-for" element={<WhoIsItFor data-id="element-170" />} data-id="element-169" />
            <Route path="/logo-gallery" element={<LogoGallery data-id="element-172" />} data-id="element-171" />
            <Route path="/privacy" element={<Privacy data-id="element-174" />} data-id="element-173" />
            <Route path="/terms" element={<Terms data-id="element-176" />} data-id="element-175" />
          </Routes>
        </MemoryRouter>
      </MUIThemeProvider>
    </ThemeProvider>;
}
;
<style jsx data-id="element-177">{`
  input[type='range'] {
    -webkit-appearance: none;
    appearance: none;
  }
  input[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    border: 2px solid white;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
    cursor: pointer;
    transition: all 0.3s ease;
  }
  input[type='range']::-webkit-slider-thumb:hover {
    transform: scale(1.15);
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
  }
  input[type='range']::-webkit-slider-thumb:active {
    transform: scale(1.1);
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  }
  input[type='range']:focus {
    outline: none;
  }
  input[type='range']:focus::-webkit-slider-thumb {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
`}</style>;